import { addDays } from '@shh/shared';
import { cn, Sheet } from '@shh/ui';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLens } from '../app/lens';
import { CalendarSearch } from '../components/calendar/CalendarSearch';
import { HelpSheet, LayersSheet } from '../components/calendar/CalendarSheets';
import { CalendarSidebar } from '../components/calendar/CalendarSidebar';
import { CalendarToolbar } from '../components/calendar/CalendarToolbar';
import { DayView } from '../components/calendar/DayView';
import { MonthPhone, MonthWide, type MonthProps } from '../components/calendar/MonthView';
import { useCalendarView } from '../components/calendar/useCalendarView';
import { WeekByPerson } from '../components/calendar/WeekByPerson';
import { YearView } from '../components/calendar/YearView';
import { EventSheet } from '../components/EventSheet';
import { EventForm } from '../components/EventForm';
import { addMonths, regionOf, weekStart } from '../data/calendar';
import { useClock } from '../data/clock';
import type { E } from '../data/derive';
import { useFamily, useFamilyFor } from '../data/family';
import { calendarText } from '../i18n/calendar';
import { setAddDay } from '../lib/addDay';
import { useMediaQuery } from '../lib/useMediaQuery';

const MONTH_KEY = 'shh:calMonthOpen';
const SIDEBAR_KEY = 'shh:calSidebar';
/** Open by default only on big screens; tablets start with the full width (Mike, 19.10.). */
const readSidebar = () => {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY);
    if (v) return v === 'true';
  } catch {
    // fine without
  }
  return window.matchMedia?.('(min-width: 1536px)').matches ?? false;
};
const readOpen = () => {
  try {
    return localStorage.getItem(MONTH_KEY) !== 'false';
  } catch {
    return true;
  }
};

/**
 * The family calendar (Kalender 1, 1.24): day, week by person, month and year – on every
 * device the same four views (Mike, 18.10.). The day is the very day plan of Heute; the week
 * answers "wer ist wann wo?"; the month folds with the finger on phones; the year plans ahead.
 */
export function CalendarPage() {
  const base = useFamily();
  const lens = useLens();
  const now = useClock(base.timeZone);
  const cal = useCalendarView();
  const { view, date, weekOf, layers, people } = cal;
  const wide = useMediaQuery('(min-width: 1024px)');
  const [sidebarOpen, setSidebarOpen] = useState(readSidebar);
  const withSidebar = wide && sidebarOpen;
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    try {
      localStorage.setItem(SIDEBAR_KEY, String(!sidebarOpen));
    } catch {
      // fine without
    }
  };

  // the dates on screen – older ones are loaded on demand
  const range: [string, string] =
    view === 'tag'
      ? [date, date]
      : view === 'woche'
        ? [weekOf, addDays(weekOf, 6)]
        : view === 'monat'
          ? [weekStart(`${date.slice(0, 8)}01`), addDays(addMonths(`${date.slice(0, 8)}01`, 1), 6)]
          : [`${date.slice(0, 4)}-01-01`, `${date.slice(0, 4)}-12-31`];
  const f = useFamilyFor(range[0], range[1]);

  const [openEvent, setOpenEvent] = useState<{ id: string; date: string } | null>(null);
  const [editing, setEditing] = useState<E | null>(null);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [layersOpen, setLayersOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [monthOpen, setMonthOpenState] = useState(readOpen);
  const setMonthOpen = (open: boolean) => {
    setMonthOpenState(open);
    try {
      localStorage.setItem(MONTH_KEY, String(open));
    } catch {
      // fine without
    }
  };

  // a new event from the app's ＋ starts on the day shown here
  useEffect(() => {
    setAddDay(date);
    return () => setAddDay(undefined);
  }, [date]);

  // keyboard on computers: arrows turn pages, T today, D/W/M/J (Y) the views
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.ctrlKey || e.metaKey || e.altKey || t?.closest('input, textarea, [role="dialog"]'))
        return;
      const k = e.key.toLowerCase();
      if (e.key === 'ArrowLeft') cal.step(-1);
      else if (e.key === 'ArrowRight') cal.step(1);
      else if (k === 't') cal.go({ date: base.today });
      else if (k === 'd') cal.go({ view: 'tag' }, true);
      else if (k === 'w') cal.go({ view: 'woche' }, true);
      else if (k === 'm') cal.go({ view: 'monat' }, true);
      else if (k === 'j' || k === 'y') cal.go({ view: 'jahr' }, true);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cal, base.today]);

  // phone month: the calendar bar comes back in when the month is pulled open, wherever the list
  // is scrolled (Mike, 19.10.: "kommt immer das Kopfmenü mit rein")
  const [barPinned, setBarPinned] = useState(false);
  const bar = useRef<HTMLDivElement>(null);
  const [barH, setBarH] = useState(0);
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const measure = () => setBarH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const open = (id: string, d: string) => setOpenEvent({ id, date: d });
  const searchShown = withSidebar ? query.trim().length > 0 : searching;
  const pinned = view === 'monat' && !wide && !searchShown && barPinned;
  const common = { f, now, layers, people, onOpen: open };

  const main = searchShown ? (
    <CalendarSearch
      f={f}
      query={query}
      onQuery={setQuery}
      people={people}
      now={now}
      lens={lens.id}
      onOpen={open}
      field={!withSidebar}
    />
  ) : view === 'tag' ? (
    <DayView {...common} date={date} lens={lens.id} onStep={(dir) => cal.step(dir)} />
  ) : view === 'woche' ? (
    <WeekByPerson {...common} weekOf={weekOf} onStep={(dir) => cal.step(dir)} />
  ) : view === 'monat' ? (
    (() => {
      const month: MonthProps = {
        ...common,
        date,
        lens: lens.id,
        open: monthOpen,
        onOpenChange: setMonthOpen,
        onPick: (d) => cal.go({ date: d }),
        onDay: (d) => cal.go({ view: 'tag', date: d }, true),
        // the week number opens that week – at today if it is in it (Mike, 18.10.)
        onWeek: (monday) =>
          cal.go(
            {
              view: 'woche',
              date: base.today >= monday && base.today <= addDays(monday, 6) ? base.today : monday,
            },
            true,
          ),
        onStep: (dir, unit) => cal.step(dir, unit),
        barPinned: pinned,
        onBarPinned: setBarPinned,
        // the month docks below the app header – and below the calendar bar while that is pinned
        barOffset: pinned ? barH : 0,
      };
      return wide ? <MonthWide {...month} /> : <MonthPhone {...month} />;
    })()
  ) : (
    <YearView
      {...common}
      date={date}
      onMonth={(first) => cal.go({ view: 'monat', date: first }, true)}
      onWeekend={(sat) => cal.go({ view: 'woche', date: sat }, true)}
      onStep={(dir) => cal.step(dir)}
    />
  );

  return (
    <>
      <div
        className={cn(
          // the next page slides in from the right: it must not widen the document, or phones
          // shift everything left and the tab bar slips away (Mike, 19.10., video)
          'grid grid-cols-1 gap-section overflow-x-clip',
          withSidebar && 'lg:grid-cols-[17rem_minmax(0,1fr)]',
        )}
      >
        {withSidebar && (
          <CalendarSidebar
            f={f}
            date={date}
            range={view === 'jahr' ? null : range}
            people={people}
            query={query}
            onQuery={setQuery}
            onPick={(d) => cal.go({ date: d })}
            showTodos={view !== 'tag'}
          />
        )}
        <div className="flex min-w-0 flex-col gap-gap-lg">
          <div
            ref={bar}
            className={cn(pinned && 'sticky top-(--shh-header-h) z-15 bg-surface-canvas')}
          >
            <CalendarToolbar
              view={view}
              date={date}
              weekOf={weekOf}
              searchOpen={searchShown}
              onView={(v) => {
                setSearching(false);
                setQuery('');
                cal.go({ view: v }, true);
              }}
              onStep={(dir) => cal.step(dir)}
              onToday={() => cal.go({ date: base.today })}
              onSearch={() => setSearching((s) => !s)}
              onLayers={() => setLayersOpen(true)}
              onHelp={() => setHelpOpen(true)}
              sidebar={withSidebar}
              onSidebar={wide ? toggleSidebar : undefined}
            />
          </div>
          {main}
        </div>
      </div>

      <EventSheet
        eventId={openEvent?.id ?? null}
        date={openEvent?.date ?? date}
        onClose={() => setOpenEvent(null)}
        onEdit={(e) => {
          setOpenEvent(null);
          setEditing(e);
        }}
      />
      <Sheet
        isOpen={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={calendarText.editEvent}
        isDismissable={false}
      >
        {editing && <EventForm initial={editing} onDone={() => setEditing(null)} />}
      </Sheet>
      <LayersSheet
        isOpen={layersOpen}
        onOpenChange={setLayersOpen}
        layers={layers}
        onLayer={cal.setLayer}
        noRegion={!regionOf(base)}
        canParent={lens.canParent}
        onHelp={() => {
          setLayersOpen(false);
          setHelpOpen(true);
        }}
      />
      <HelpSheet isOpen={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
